import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { saveStudentSession } from '@/lib/auth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, ShieldCheck, KeyRound, CheckCircle2, XCircle } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';

export default function StudentSetupPage() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading | ready | invalid | done
    const [ctx, setCtx] = useState(null);
    const [form, setForm] = useState({ password: '', password2: '', college_id: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            if (!token) {
                setStatus('invalid');
                return;
            }
            try {
                const { data } = await api.post('/student/setup/verify', { token });
                setCtx(data);
                setStatus('ready');
            } catch (e) {
                setStatus('invalid');
            }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) {
            toast.error('Password must be at least 8 characters.');
            return;
        }
        if (form.password !== form.password2) {
            toast.error('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post('/student/setup', {
                token,
                password: form.password,
                college_id: form.college_id.trim(),
            });
            saveStudentSession(data.token, data.student);
            setStatus('done');
            toast.success('Password set! You’re now signed in.');
            setTimeout(() => navigate('/'), 900);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to set password');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-orb px-4 py-10 sm:px-6">
            <div className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-blob-lavender" />
            <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-blob-periwinkle blur-3xl" />

            <div className="relative z-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <BrandLogo size="lg" to="/" className="mx-auto" />
                    <p className="mt-3 text-sm text-muted-foreground">Set up your account</p>
                </div>
                <Card className="border-border/60 shadow-[0_20px_60px_rgba(12,12,44,0.08)]">
                    <CardContent className="p-8">
                        {status === 'loading' && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Verifying your invitation…
                            </div>
                        )}
                        {status === 'invalid' && (
                            <div>
                                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                                    <XCircle className="h-6 w-6" />
                                </div>
                                <h1 className="font-display text-xl font-semibold text-primary">This link is no longer valid</h1>
                                <p className="mt-1 text-sm text-muted-foreground">Ask your admin to send you a fresh magic link, or try requesting a new one.</p>
                                <div className="mt-4 flex flex-col gap-2">
                                    <Link to="/student/forgot">
                                        <Button variant="secondary" className="w-full">Request a new link</Button>
                                    </Link>
                                    <Link to="/">
                                        <Button variant="ghost" className="w-full">Back to sign in</Button>
                                    </Link>
                                </div>
                            </div>
                        )}
                        {status === 'ready' && ctx && (
                            <>
                                <div className="mb-4 flex items-center gap-2 rounded-full border border-accent/30 bg-secondary px-3 py-1 text-xs font-medium text-primary w-fit">
                                    <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                                    Invitation verified
                                </div>
                                <h1 className="font-display text-2xl font-semibold text-primary">Hi {ctx.name?.split(' ')[0]}, set your password</h1>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    We’ll sign you in as <strong className="text-foreground">{ctx.email}</strong> once you’re done.
                                </p>
                                <form onSubmit={submit} className="mt-6 space-y-4">
                                    <div>
                                        <Label htmlFor="college_id">Confirm your College ID</Label>
                                        <Input
                                            id="college_id"
                                            required
                                            placeholder={`Hint: ${ctx.college_id_hint}`}
                                            value={form.college_id}
                                            onChange={(e) => setForm((f) => ({ ...f, college_id: e.target.value }))}
                                            data-testid="setup-college-id"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="password">Choose a password (min 8 chars)</Label>
                                        <Input
                                            id="password"
                                            type="password"
                                            required
                                            minLength={8}
                                            value={form.password}
                                            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                            data-testid="setup-password"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="password2">Repeat password</Label>
                                        <Input
                                            id="password2"
                                            type="password"
                                            required
                                            minLength={8}
                                            value={form.password2}
                                            onChange={(e) => setForm((f) => ({ ...f, password2: e.target.value }))}
                                            data-testid="setup-password2"
                                        />
                                    </div>
                                    <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="setup-submit">
                                        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                                        Set password & sign in
                                    </Button>
                                </form>
                            </>
                        )}
                        {status === 'done' && (
                            <div className="text-center">
                                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-accent/20 text-accent">
                                    <CheckCircle2 className="h-6 w-6" />
                                </div>
                                <h1 className="font-display text-xl font-semibold text-primary">You’re in!</h1>
                                <p className="mt-1 text-sm text-muted-foreground">Taking you to the mentor list…</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
